<template>
  <div class="pt-2 mainContainer">
    <add-subsystem-dialog
      v-if="selectedSystem != null"
      :system="selectedSystem"
      :showDialog="addSubsystemDialog"
      @close-dialog="addSubsystemDialog = false"
    />
    <!-- empty (no projects)-->
    <div
      class="emptyProjectContainer"
      v-if="!configurator.currentProject && !configurator.currentProjectLoading && !configurator.currentSystemLoading"
      fill-height
    >
      <v-btn
        width="420"
        outlined
        @click="openProject"
        color="#a67ee7"
        class="newProjectButton sigfloH3 text-capitalize px-12 py-12"
        ><v-icon class="mr-2" x-large>mdi-plus-circle-outline</v-icon>Start New Project</v-btn
      >
      <div class="text1 sigfloXl black--text">
        Build Your <span class="blueText">Dream<br />AVL Setup</span> with Sigflō
      </div>
      <v-img width="618" height="706" class="emptyProjectImage" src="@/assets/images/systemBubbles.png" />
    </div>
    <!-- no project selected -->
    <div
      class="emptyProjectContainer"
      v-if="
        !configurator.currentProject && !configurator.currentProjectLoading && configurator.projects.length > 0 && false
      "
      fill-height
    >
      <v-container fluid fill-height class="ml-9 mt-6">
        <v-row class="mb-5">
          <div class="sigfloH4 ml-5">Projects</div>
        </v-row>
        <newProjectCard />
      </v-container>
    </div>
    <!-- Create System Dialog -->
    <v-dialog v-model="configurator.createSystemDialog" max-width="600">
      <v-card class="pa-2" outlined :loading="configurator.savingNewSystem">
        <v-card-title class="justify-center">New System</v-card-title>
        <v-container class="" v-if="configurator.availableSystemDefinitions.length > 0">
          <v-row class="justify-center">
            <v-col v-for="systemDefinition in configurator.availableSystemDefinitions" :key="systemDefinition.id">
              <addSystemSquare :system="systemDefinition" />
            </v-col>
          </v-row>
        </v-container>
      </v-card>
    </v-dialog>
    <!-- loader -->
    <v-progress-linear indeterminate v-show="configurator.currentProjectLoading"></v-progress-linear>
    <!-- Loaded Project Page -->
    <v-container v-if="configurator.currentProject" class="ml-1">
      <!-- loading overlay -->
      <v-overlay v-model="configurator.currentProjectLoading">
        <v-progress-linear indeterminate></v-progress-linear>
      </v-overlay>
      <!-- Project Header -->
      <!-- new project system list-->
      <v-container>
        <v-row>
          <div class="mr-4">
            <projectSummaryCard :project="configurator.currentProject" />
          </div>
          <v-col>
            <div class="d-flex">
              <div class="sigfloH4 mb-4 mr-4">Systems</div>
              <sig-help-dialog-button logo-side="left" :title="sigTitle" :message="sigMessage" />
            </div>
            <v-row class="systemCards">
              <!-- system list-->
              <v-col class="ma-0 p-0 pb-5 mr-2 systemColumn" v-for="system in configurator.systems" :key="system.id">
                <emptySystemCard
                  @add-subsystem="showAddSubsystemDialog"
                  :system="system"
                  v-show="!hasSubsystems(system)"
                />
                <system-card @add-subsystem="showAddSubsystemDialog" :system="system" v-show="hasSubsystems(system)" />
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-container>
    </v-container>
  </div>
</template>

<script>
  import addSubsystemDialog from '@/components/configurator/project/AddSubsystemDialog.vue';
  import newProjectCard from '../../components/configurator/project/NewProjectCard.vue';
  import projectSummaryCard from '@/components/configurator/project/ProjectSummaryCard';
  import emptySystemCard from '@/components/configurator/project/EmptySystemCard';
  import systemCard from '@/components/configurator/project/SystemCard';
  import sigHelpDialogButton from '@/components/shared/SigHelpDialogButton';
  import { mapState } from 'vuex';

  export default {
    props: {
      projectTemplateId: String,
      landingPage: String
    },
    created() {
      // Check if route has order ID parameter
      const projectTemplateId = this.$route.query.projectTemplateId;
      const landingPage = this.$route.query.landingPage;
      const login = this.$route.query.login;
      if (login == 'true') {
        this.removeQueryParam('login');
        this.showLoginDialog();
      }
      if (projectTemplateId) {
        this.$store.commit('configurator/setCurrentProjectLoading', true);
        this.$store
          .dispatch('configurator/createFromTemplate', projectTemplateId)
          .then((project) => {
            if (project.sigMessageModel) {
              this.processSigResponse(project.sigMessageModel);
            }
            if (landingPage == 'project') {
              if (project.id) {
                // open the new project
                this.openProject(project.id);
              }
            } else if (landingPage == 'subsystem') {
              // open the first subsystem
              if (project.id) {
                // open the new project
                this.openProject(project.id, true);
              }
            } else {
              // if no landing page, reload the project list
              this.$store.dispatch('configurator/getProjects');
            }
          })
          .finally(() => {
            this.$store.commit('configurator/setCurrentProjectLoading', false);
          });
      }

      // remove the params from the URL
      if (projectTemplateId || landingPage) {
        const updatedQuery = { ...this.$route.query };
        delete updatedQuery.projectTemplateId;
        delete updatedQuery.landingPage;
        this.$router.replace({ path: this.$route.path, query: updatedQuery });
      }
    },
    methods: {
      /**
       * Opens the add subsystem dialog and dispatches an action to get the available subsystems.
       *
       * @returns {void}
       */
      showAddSubsystemDialog(system) {
        this.selectedSystem = system;
        this.addSubsystemDialog = true;
        this.$store.dispatch('configurator/getAvailableSubsystems', system.systemDefinitionId);
      },
      /**
       * Checks if a system has any subsystems.
       * @param {Object} system - The system object to check.
       * @returns {boolean} - Returns true if the system has subsystems, otherwise false.
       */
      hasSubsystems(system) {
        if (!system) return false;
        const subsystems = this.configurator.subsystems[`subsystem_${system.id}`];
        return subsystems && subsystems.length > 0;
      },
      /**
       * Shows confirmation window for project deletion
       */
      confirmDelProject() {
        const message = `Delete Project ${this.project.name}?`;
        this.showPlainDialog({
          display: 'Now',
          message: message,
          messageTitle: 'Confirm',
          parentId: 0,
          parentType: '',
          published: true,
          buttonArray: [
            {
              label: 'Delete',
              callback: this.deleteProject,
              icon: 'mdi-close',
              color: 'red'
            },
            {
              label: 'Cancel',
              callback: this.closePlainDialog,
              icon: '',
              color: 'grey'
            }
          ]
        });
      },
      /**
       * Shows create new project dialog
       */
      showCreateSystemDialog: function (val) {
        this.$store.commit('configurator/setCreateSystemDialog', val);
      },
      /**
       * Retrieves systems for current project
       */
      openSystems: function () {
        this.$store.dispatch('configurator/getSubsystems', this.configurator.currentProject.id);
      },
      /**
       * Builds the dropdown list objects for specified setting
       */
      getProjectSettingValueModels: function (setting) {
        var items = [...setting.projectSettingValueModels];
        // var label = (this.hasProducts) ? "All" : "None"
        // var emptyRecord = {id: 0, projectSettingDefinitionId: items[0].projectSettingDefinitionId, label: label, value: null, published: true}
        // if (items && items[0].value != null)
        //   items.unshift(emptyRecord)

        return items;
      },
      /**
       * Updates datamodel for selected record
       */
      updateProjectSettingModel: function (newValue, selectedRecord) {
        // convert multiselect data to delimited string
        if (Array.isArray(newValue)) newValue = newValue.join(',');

        this.$store.commit('configurator/updateProjectSettingModel', {
          updatedProjectSettingModel: selectedRecord,
          newValue: newValue
        });
        this.$store.commit('configurator/setCurrentProjectLoading', true);
        this.$store.dispatch('configurator/updateProject').then(() => {
          this.$store.commit('configurator/setCurrentProjectLoading', false);
        });
      }
    },
    mounted: function () {},
    computed: {
      ...mapState(['configurator', 'navigation']),
      /**
       * Returns the title of the signal message model.
       * If the current subsystem step component does not have a signal message model, an empty string is returned.
       * Otherwise, the message title of the signal message model is returned.
       *
       * @returns {string} The title of the signal message model.
       */
      sigTitle() {
        if (!this.configurator.currentProject.sigMessageModel) {
          return '';
        }

        return this.configurator.currentProject.sigMessageModel.messageTitle;
      },
      /**
       * Returns the signature message for the current subsystem step component.
       * If the sigMessageModel is not available, an empty string is returned.
       * @returns {string} The signature message.
       */
      sigMessage() {
        if (!this.configurator.currentProject.sigMessageModel) {
          return '';
        }
        return this.configurator.currentProject.sigMessageModel.message;
      },
      /**
       * Number of systems ready for checkout
       */
      readySystems: {
        get() {
          return this.configurator.systems.filter((x) => x.percentComplete == 100).length;
        }
      },
      /**
       * Percent Complete based on configured/ready systems
       */
      projectPercentComplete: {
        get() {
          if (this.configurator.systems.length == 0) return 0;

          const totalSystems = this.configurator.systems.length;

          return Math.round((this.readySystems / totalSystems) * 100);
        }
      },
      buildButtonClass: function (systemType) {
        const prefix = 'systemButton white primary--text text-h3';
        let btnClass = '';

        switch (systemType) {
          case this.configurator.systemTypes.AUDIO:
            btnClass += ' audio';
            break;
          case this.configurator.systemTypes.VIDEO:
            btnClass += ' video';
            break;
          case this.configurator.systemTypes.LIGHTING:
            btnClass += ' lighting';
            break;
        }

        return prefix + ' ' + btnClass;
      },
      completionDate() {
        return this.formatDate(this.configurator.currentProject.completionDate);
      },
      projectBudget() {
        return this.formatPrice(this.configurator.currentProject.budget);
      }
    },
    components: {
      addSubsystemDialog,
      newProjectCard,
      projectSummaryCard,
      emptySystemCard,
      systemCard,
      sigHelpDialogButton
    },
    data() {
      return {
        addSubsystemDialog: false,
        showSteps: false,
        newSystem: {},
        projectPanel: null,
        selectedSystem: null
      };
    }
  };
</script>

<style scoped>
  .systemCards {
    max-width: 1102px;
  }
  .emptyProjectContainer {
    position: relative;
    max-width: 1640px;
  }
  .newProjectButton {
    position: absolute;
    top: 460px;
    left: 50px;
    letter-spacing: 0.5px;
    border-radius: 24px;
    background-color: #fff;
  }
  .text1 {
    position: absolute;
    top: 606px;
    left: 50px;
    z-index: 2;

    width: 847px;
    height: 178px;
  }
  .emptyProjectImage {
    position: absolute;
    top: 250px;
    left: 920px;
  }
  .projectPanel {
    min-width: 440px;
  }
  .systemHeaderBar {
    max-width: 1400px;
    padding-right: 40px;
  }

  .labelChip {
    background-color: white !important;
  }

  .mainContainer {
    position: relative;
    width: 100vw;
  }

  .projectSystemTotalLabel {
    padding-top: 16px;
    text-align: end;
  }

  .projectSystemTotal {
    border-top-left-radius: 24px;
    border-bottom-left-radius: 24px;
    line-height: 1rem;
  }

  .projectSystemTotalContainer {
    padding: 0px;
    position: fixed;
    float: right;
    top: 5px;
    right: 0px;
    max-width: 350px;
    height: 48px;
    z-index: 2;
  }

  button.v-btn.systemButton {
    /* overrides v-btn dims */
    height: 145px;
    width: 145px;
    border-radius: 10px;
  }
</style>
